import { SVG_COMPONENTS, SVG_DIMENSIONS } from "@/assets/admin";
import { ADMIN_COLORS } from '@/constants/admin';
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    View,
    Text,
    TouchableOpacity,
    ScrollView,
    Animated,
    StyleSheet,
    Dimensions,
    Modal,
  } from 'react-native';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

interface SelectorProps {
    label: string;
    value: string;
    options: Array<{ id: number; name: string }>;
    onSelect: (id: number) => void;
    disabled?: boolean;
  }

interface EnhancedSelectorProps extends SelectorProps {
    isActive: boolean;
    onToggle: (isOpen: boolean) => void;
  }
  
  export const EnhancedSelector: React.FC<EnhancedSelectorProps> = ({
    label,
    value,
    options,
    onSelect,
    disabled = false,
    isActive,
    onToggle,
  }) => {
    const dropdownAnim = useRef(new Animated.Value(0)).current;
    const rotationAnim = useRef(new Animated.Value(0)).current;
    const [selectorLayout, setSelectorLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const selectorRef = useRef<View>(null);

    // Cleanup effect to ensure proper animation state
    useEffect(() => {
      return () => {
        dropdownAnim.removeAllListeners();
        rotationAnim.removeAllListeners();
      };
    }, [dropdownAnim, rotationAnim]);
  
    // Sync animation with isActive prop
    useEffect(() => {
      const toValue = isActive ? 1 : 0;
      
      // Stop any ongoing animations first
      dropdownAnim.stopAnimation();
      rotationAnim.stopAnimation();
      
      Animated.parallel([
        Animated.timing(dropdownAnim, {
          toValue,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(rotationAnim, {
          toValue,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start((finished) => {
        // Ensure animation completes properly
        if (finished) {
          if (isActive) {
            rotationAnim.setValue(1);
          } else {
            rotationAnim.setValue(0);
          }
        }
      });
    }, [isActive, dropdownAnim, rotationAnim]);
  
    const toggleDropdown = useCallback(() => {
      if (disabled) return;
      
      // Measure selector position before opening dropdown
      if (!isActive && selectorRef.current) {
        selectorRef.current.measureInWindow((x, y, width, height) => {
          setSelectorLayout({ x, y, width, height });
        });
      }
      
      onToggle(!isActive);
    }, [disabled, isActive, onToggle]);
  
    const handleSelect = useCallback((id: number) => {
      onSelect(id);
      onToggle(false); // Close dropdown after selection
    }, [onSelect, onToggle]);
  
    const dropdownHeight = dropdownAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [0, Math.min(options.length * 48, 200)], // Max height of 200
    });
  
    const arrowRotation = rotationAnim.interpolate({
      inputRange: [0, 1],
      outputRange: ['0deg', '180deg'], // Closed: 0deg = down ▼, Open: 180deg = up ▲
    });
  
        // Calculate dropdown position and height
    const maxDropdownHeight = Math.min(options.length * 48, 200);
    const spaceBelow = SCREEN_HEIGHT - (selectorLayout.y + selectorLayout.height);
    const shouldShowAbove = spaceBelow < maxDropdownHeight && selectorLayout.y > maxDropdownHeight;
    const dropdownTop = shouldShowAbove ? selectorLayout.y - maxDropdownHeight : selectorLayout.y + selectorLayout.height;
    
    return (
      <View ref={selectorRef} style={styles.selectorContainer}>
        <View style={styles.labelContainer}>
          <Text style={styles.labelText}>{label}</Text>
        </View>
        <View style={styles.selectorInput}>
          <TouchableOpacity
            style={[styles.selectorContent, disabled && styles.selectorDisabled]}
            onPress={toggleDropdown}
            activeOpacity={disabled ? 1 : 0.7}
            accessibilityLabel={`${label}: ${value}`}
            accessibilityHint={disabled ? 'Selector is disabled' : `Tap to change ${label.toLowerCase()}`}
            accessibilityRole="button"
            accessibilityState={{ disabled, expanded: isActive }}
          >
            <Text style={[styles.selectorText, disabled && styles.selectorTextDisabled]}>
              {value}
            </Text>
            <Animated.View style={{ transform: [{ rotate: arrowRotation }] }}>
              <SVG_COMPONENTS.ARROW_DROP_DOWN
                {...SVG_DIMENSIONS.ARROW_DROP_DOWN}
                style={disabled ? { opacity: 0.5 } : undefined}
              />
            </Animated.View>
          </TouchableOpacity>
        </View>

        <Modal
          visible={isActive}
          transparent={true}
          animationType="none"
          onRequestClose={() => onToggle(false)}
          onDismiss={() => {
            rotationAnim.setValue(0);
            dropdownAnim.setValue(0);
          }}
        >
          <TouchableOpacity 
            style={styles.modalOverlay} 
            activeOpacity={1} 
            onPress={() => onToggle(false)}
          >
            <Animated.View 
              style={[
                styles.dropdownModal,
                {
                  top: dropdownTop,
                  left: selectorLayout.x,
                  width: selectorLayout.width,
                  height: dropdownHeight,
                  opacity: dropdownAnim,
                }
              ]}
            >
              <ScrollView 
                style={styles.dropdownScrollView}
                showsVerticalScrollIndicator={false}
                nestedScrollEnabled={true}
              >
                {options.map((option) => (
                  <TouchableOpacity
                    key={option.id}
                    style={[
                      styles.dropdownItem,
                      value === option.name && styles.dropdownItemSelected
                    ]}
                    onPress={() => handleSelect(option.id)}
                    accessibilityLabel={option.name}
                    accessibilityRole="button"
                    accessibilityState={{ selected: value === option.name }}
                  >
                    <Text style={[
                      styles.dropdownText,
                      value === option.name && styles.dropdownTextSelected
                    ]}>
                      {option.name}
                    </Text>
                    {value === option.name && (
                      <View style={styles.selectedIndicator} />
                    )}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </Animated.View>
          </TouchableOpacity>
        </Modal>
      </View>
    );
  };

  const styles = StyleSheet.create({
    selectorContainer: {
      width: '100%',
      position: 'relative',
      overflow: 'visible',
    },
    selectorInput: {
      width: '100%',
      height: 56,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: ADMIN_COLORS.SELECTOR.BORDER,
      backgroundColor: ADMIN_COLORS.SELECTOR.BACKGROUND,
    },
    selectorDisabled: {
      opacity: 0.6,
    },
    labelContainer: {
      position: 'absolute',
      top: -8,
      left: 8,
      backgroundColor: ADMIN_COLORS.SELECTOR.BACKGROUND,
      paddingHorizontal: 4,
      zIndex: 1,
    },
    labelText: {
      fontFamily: 'System',
      fontWeight: '400',
      fontSize: 12,
      lineHeight: 16,
      color: ADMIN_COLORS.SELECTOR.LABEL,
      includeFontPadding: false,
    },
    selectorContent: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 12,
      paddingVertical: 16,
    },
    selectorText: {
      fontFamily: 'System',
      fontWeight: '500',
      fontSize: 16,
      lineHeight: 24,
      color: ADMIN_COLORS.SELECTOR.TEXT,
      flex: 1,
      includeFontPadding: false,
    },
    selectorTextDisabled: {
      color: ADMIN_COLORS.SELECTOR.TEXT_DISABLED,
    },
    iconDisabled: {
      opacity: 0.5,
    },
    
    // Modal overlay styles
    modalOverlay: {
      flex: 1,
      backgroundColor: ADMIN_COLORS.BACKGROUND.MODAL_OVERLAY,
    },
    
    // Dropdown modal styles
    dropdownModal: {
      position: 'absolute',
      backgroundColor: ADMIN_COLORS.SELECTOR.DROPDOWN_BACKGROUND,
      borderWidth: 1,
      borderColor: ADMIN_COLORS.SELECTOR.DROPDOWN_BORDER,
      borderRadius: 8,
      overflow: 'hidden',
      elevation: 10, // Android shadow - higher than menu
      shadowColor: ADMIN_COLORS.SHADOW.DEFAULT, // iOS shadow
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.3,
      shadowRadius: 6,
      zIndex: 9999, // Highest z-index to appear above everything
    },
    dropdownScrollView: {
      flex: 1,
    },
    dropdownItem: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 12,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: ADMIN_COLORS.BORDER,
      position: 'relative',
      minHeight: 48,
    },
    dropdownItemSelected: {
      backgroundColor: ADMIN_COLORS.SELECTOR.ITEM_SELECTED_BACKGROUND,
    },
    dropdownText: {
      fontSize: 16,
      fontWeight: '400',
      color: ADMIN_COLORS.SELECTOR.TEXT,
      flex: 1,
    },
    dropdownTextSelected: {
      fontWeight: '600',
      color: ADMIN_COLORS.SELECTOR.ITEM_SELECTED_TEXT,
    },
    selectedIndicator: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 3,
      backgroundColor: ADMIN_COLORS.SELECTOR.SELECTED_INDICATOR,
    },
  });
  